import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { Link } from 'react-router-dom';
import Sidebar from './Sidebar';
import TopSection from './TopSection';
import StudentProfile from './StudentProfile';
import StudentHome from './StudentHome';
import StudentPayments from './StudentPayments';
import StudentClasses from './StudentClasses';

import StudentSupport from './StudentSupport';
import '../styles/StudentDashboard.css';

export default function StudentDashboard() {
  const [classes, setClasses] = useState([]);
  const [studentDetails, setStudentDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeView, setActiveView] = useState('home');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const { data: { user }, error: authError } = await supabase.auth.getUser();

      if (authError) throw new Error(`Authentication error: ${authError.message}`);

      if (!user) {
        setError('No user found. Please log in.');
        return;
      }

      // Fetch student details including payment information
      const { data: studentData, error: studentError } = await supabase
        .from('students')
        .select('*')
        .eq('id', user.id)
        .single();

      if (studentError) throw new Error(`Error fetching student details: ${studentError.message}`);

      // Fetch classes
      const { data: classesData, error: classesError } = await supabase
        .from('individual_classes')
        .select(`
          *,
          teachers:teacher_id (full_name, phone_number)
        `)
        .eq('student_id', user.id)
        .order('class_date', { ascending: true })
        .order('start_time', { ascending: true });

      if (classesError) throw new Error(`Error fetching classes: ${classesError.message}`);

      setStudentDetails(studentData);
      setClasses(classesData || []);
    } catch (error) {
      console.error('Error fetching data:', error.message);
      setError(`Failed to fetch data: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await supabase.auth.signOut();
      // Redirect to login page or update state to reflect logged out status
    } catch (error) {
      console.error('Error logging out:', error.message);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-900">
        <div className="text-center">
          <svg className="w-20 h-20 mx-auto mb-4" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
            <circle className="text-gray-600" stroke="currentColor" strokeWidth="4" fill="none" cx="50" cy="50" r="45" />
            <path className="text-blue-500" fill="currentColor" d="M50,25 L75,50 L50,75 L25,50 Z">
              <animateTransform
                attributeName="transform"
                type="rotate"
                from="0 50 50"
                to="360 50 50"
                dur="1s"
                repeatCount="indefinite"
              />
            </path>
          </svg>
          <h2 className="text-2xl font-semibold text-white mb-2">Student LMS</h2>
          <p className="text-gray-400">Loading your personalized learning experience...</p>
        </div>
        <style jsx>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          svg circle {
            animation: dash 1.5s ease-in-out infinite;
          }
          @keyframes dash {
            0% {
              stroke-dasharray: 1, 150;
              stroke-dashoffset: 0;
            }
            50% {
              stroke-dasharray: 90, 150;
              stroke-dashoffset: -35;
            }
            100% {
              stroke-dasharray: 90, 150;
              stroke-dashoffset: -124;
            }
          }
        `}</style>
      </div>
    );
  }

  if (error) {
    return <div className="error">{error}</div>;
  }

  return (
    <div className="flex h-screen bg-gray-100">
    <Sidebar 
      studentName={studentDetails.full_name}
      onViewChange={setActiveView}
      activeView={activeView}
    />
    <div className="flex flex-col flex-grow overflow-hidden">
      <TopSection 
        studentName={studentDetails.full_name} 
        grade={studentDetails.grade}
        onLogout={handleLogout}
        classlink={studentDetails.jitsi_link} 
      />
      <main className="flex-grow p-6 overflow-auto bg-gray-100">
        {activeView === 'home' && <StudentHome studentDetails={studentDetails} classes={classes} />}
        {activeView === 'profile' && <StudentProfile studentDetails={studentDetails} />}
        {activeView === 'classes' && <StudentClasses classes={classes} />}
        {activeView === 'payments' && <StudentPayments studentDetails={studentDetails} />}
        {activeView === 'help' && <StudentSupport />}
        

      </main>
    </div>
  </div>
);
}